import * as React from "react";
import { Button, Input, Spinner, makeStyles } from "@fluentui/react-components";
import { Calendar20Regular, Checkmark16Regular, Dismiss16Regular, QuestionCircle16Regular } from "@fluentui/react-icons";
import {
    graphMailService,
    type EinladungsAntwort,
    type TerminEinladung,
} from "../../services/graphMailService";
import {
    ANTWORT_LABEL,
    aktiveAntwort,
    beantwortbar,
    einladungsFehlerText,
    mussSenden,
    statusText,
    terminZeitText,
} from "./terminEinladung";

const useStyles = makeStyles({
    leiste: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginBottom: "12px",
        padding: "10px 12px",
        borderRadius: "10px",
        border: "1px solid #e2bcd7",
        backgroundColor: "#f8eef5",
        color: "#241522",
        fontSize: "13px",
    },
    kopf: { display: "flex", alignItems: "flex-start", gap: "8px" },
    titel: { fontWeight: 600 },
    leise: { color: "#6b5a66" },
    knoepfe: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" },
    kommentar: { flex: "1 1 220px", minWidth: "160px" },
    fehler: { color: "#c23b3b" },
});

/**
 * T-229 (07.10.2026: „wo kann man denn den Termin zusagen?") — die Leiste
 * über einer Termineinladung, wie in Outlook: wann, wo, von wem, und
 * Zusagen / Mit Vorbehalt / Absagen. Die Antwort setzt den Status am
 * Kalendereintrag des Postfachs; Graph schickt sie an den Organisator.
 *
 * Erscheint nur bei `meetingMessageType === "meetingRequest"`; geladen wird
 * erst dann (ein Graph-Aufruf je geöffneter Einladung, nicht je Mail). Ohne
 * Senderecht auf das Postfach — etwa beim Lesen über einen Vorgang — zeigt sie
 * den Stand, aber keine Knöpfe: geantwortet würde im Namen des Kollegen.
 */
export function TerminEinladungLeiste({
    mailbox,
    messageId,
    meetingMessageType,
    onBeantwortet,
}: {
    mailbox: string;
    messageId: string;
    meetingMessageType?: string | null;
    /**
     * Nach einer GESENDETEN Antwort (02.11.2026, wie Outlook): der Aufrufer
     * löscht die Einladung und schließt die Mail. Wirft er, bleibt die Leiste
     * stehen und sagt, dass nur das Löschen scheiterte. Ohne Rückruf bleibt die
     * Mail liegen und die Leiste zeigt den neuen Stand.
     */
    onBeantwortet?: () => Promise<void>;
}) {
    const styles = useStyles();
    const istEinladung = meetingMessageType === "meetingRequest";
    const [daten, setDaten] = React.useState<{ einladung: TerminEinladung | null; darfAntworten: boolean } | null>(null);
    const [kommentar, setKommentar] = React.useState("");
    const [laeuft, setLaeuft] = React.useState<EinladungsAntwort | null>(null);
    const [fehler, setFehler] = React.useState<string | null>(null);

    React.useEffect(() => {
        setDaten(null);
        setFehler(null);
        setKommentar("");
        if (!istEinladung || !mailbox || !messageId) return;
        let aktiv = true;
        graphMailService
            .fetchEinladung(mailbox, messageId)
            .then((d) => aktiv && setDaten(d))
            // Beiwerk: Ohne Leiste bleibt die Mail lesbar; Zusagen geht dann in Outlook.
            .catch(() => aktiv && setDaten({ einladung: null, darfAntworten: false }));
        return () => {
            aktiv = false;
        };
    }, [istEinladung, mailbox, messageId]);

    if (!istEinladung) return null;
    if (!daten) {
        return (
            <div className={styles.leiste}>
                <Spinner size="tiny" label="Einladung wird geladen…" labelPosition="after" />
            </div>
        );
    }
    const e = daten.einladung;
    if (!e) return null;

    const antworten = async (antwort: EinladungsAntwort) => {
        setFehler(null);
        // T-271: Die schon gegebene Antwort ohne Kommentar nicht erneut senden.
        if (!mussSenden(antwort, e.status, kommentar)) return;
        setLaeuft(antwort);
        try {
            const r = await graphMailService.beantworteEinladung(mailbox, messageId, antwort, kommentar.trim());
            setDaten({ ...daten, einladung: r.einladung });
            setKommentar("");
        } catch (err: unknown) {
            console.error("[TerminEinladungLeiste] Antwort fehlgeschlagen", err);
            setFehler(einladungsFehlerText(err));
            setLaeuft(null);
            return;
        }
        try {
            await onBeantwortet?.();
        } catch (err: unknown) {
            console.error("[TerminEinladungLeiste] Einladung nicht gelöscht", err);
            setFehler("Die Antwort ist gesendet, die Einladung ließ sich aber nicht löschen.");
        } finally {
            setLaeuft(null);
        }
    };

    const zeit = terminZeitText(e);
    const org = e.organisator?.name || e.organisator?.adresse;
    const stand = e.abgesagt ? "Dieser Termin wurde abgesagt." : statusText(e.status);
    const aktiv = aktiveAntwort(e.status);
    const icons: Record<EinladungsAntwort, React.ReactElement> = {
        zusagen: <Checkmark16Regular />,
        vorbehalt: <QuestionCircle16Regular />,
        absagen: <Dismiss16Regular />,
    };

    return (
        <div className={styles.leiste} role="region" aria-label="Termineinladung">
            <div className={styles.kopf}>
                <Calendar20Regular aria-hidden />
                <div>
                    <div className={styles.titel}>Einladung: {e.betreff}</div>
                    <div className={styles.leise}>
                        {[zeit, e.ort, org ? `von ${org}` : null].filter(Boolean).join(" · ")}
                    </div>
                    {stand && <div>{stand}</div>}
                </div>
            </div>
            {beantwortbar(e) && daten.darfAntworten && (
                <div className={styles.knoepfe}>
                    {(Object.keys(ANTWORT_LABEL) as EinladungsAntwort[]).map((a) => (
                        <Button
                            key={a}
                            size="small"
                            appearance={aktiv === a ? "primary" : "secondary"}
                            icon={laeuft === a ? <Spinner size="extra-tiny" /> : icons[a]}
                            disabled={laeuft != null}
                            aria-pressed={aktiv === a}
                            onClick={() => void antworten(a)}
                        >
                            {ANTWORT_LABEL[a]}
                        </Button>
                    ))}
                    <Input
                        className={styles.kommentar}
                        size="small"
                        value={kommentar}
                        placeholder="Nachricht an den Organisator (optional)"
                        aria-label="Nachricht an den Organisator"
                        onChange={(_, d) => setKommentar(d.value)}
                    />
                </div>
            )}
            {beantwortbar(e) && daten.darfAntworten && e.antwortErwuenscht === false && (
                <div className={styles.leise}>
                    Der Organisator erwartet keine Antwort — die Wahl gilt nur für Ihren Kalender.
                </div>
            )}
            {beantwortbar(e) && !daten.darfAntworten && (
                <div className={styles.leise}>
                    Antworten kann nur, wer aus diesem Postfach senden darf.
                </div>
            )}
            {fehler && <div className={styles.fehler}>{fehler}</div>}
        </div>
    );
}
